/**
 * A short two-note chime, made in the page rather than loaded.
 *
 * No audio file: the site's CSP allows media from itself only, and a file
 * would be one more thing to serve and cache. Two sine notes with a quick
 * fade are the whole sound — the same kind of "pop" every messenger uses.
 *
 * Browsers refuse to make a sound before the person has touched the page,
 * and the Android WebView follows the same rule. So the context is made on
 * the first gesture (`unlockChime`) and kept; a chime asked for before that
 * is simply not played, never queued for later — a burst of stale pops on
 * the first tap would be worse than silence.
 */

let context: AudioContext | null = null;

/** Called once from a real user gesture, so the browser lets sound through. */
export function unlockChime(): void {
  if (context) return;
  const Ctor = window.AudioContext;
  if (!Ctor) return;
  try {
    context = new Ctor();
    if (context.state === 'suspended') void context.resume().catch(() => undefined);
  } catch {
    context = null;
  }
}

function note(at: number, frequency: number, length: number): void {
  if (!context) return;
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.18, at + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(at);
  oscillator.stop(at + length + 0.02);
}

/** Plays the chime, or nothing if sound has not been unlocked by a gesture yet. */
export function chime(): void {
  if (!context) return;
  if (context.state === 'suspended') {
    void context.resume().catch(() => undefined);
  }
  const now = context.currentTime;
  note(now, 880, 0.12);
  note(now + 0.11, 1174.66, 0.18);
}
